import { useState, useCallback, useEffect, useMemo } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PolygonLayer } from '@deck.gl/layers';
import axios from 'axios';
import debounce from 'lodash/debounce'; // Import debounce

axios.defaults.baseURL = process.env.REACT_APP_API;


const LNG_CELL = 0.00045;
const LAT_CELL = 0.00025;

const DeckGLOverlay = (props) => {
    const map = useMap();
    const overlay = useMemo(() => new GoogleMapsOverlay(props), [props]);

    useEffect(() => {
        if (map) {
            overlay.setMap(map);
            return () => overlay.setMap(null);
        }
    }, [map, overlay]);

    overlay.setProps(props);
    return null;
};

const DKMap = () => {
    const [center, setCenter] = useState({ lat: 56.2639, lng: 9.5018 });
    const [zoom, setZoom] = useState(7);
    const [points, setPoints] = useState([]); // Points to display
    const [rangelat, setRangeLat] = useState(LAT_CELL);
    const [rangelng, setRangeLng] = useState(LNG_CELL);

    useEffect(() => {
        if (zoom > 15.0) {
            setRangeLat(LAT_CELL);
            setRangeLng(LNG_CELL);
        } else if (zoom > 12.0) {
            setRangeLat(LAT_CELL * 10);
            setRangeLng(LNG_CELL * 10);
        } else if (zoom > 10.0) {
            setRangeLat(LAT_CELL * 20);
            setRangeLng(LNG_CELL * 20);
        } else if (zoom > 5.0) {
            setRangeLat(LAT_CELL * 80);
            setRangeLng(LNG_CELL * 80);
        }
    }, [zoom]);

    const handleMapChange = (event) => {
        const map = event.map; // Access the map instance correctly
        setCenter(map.getCenter().toJSON());
        setZoom(map.getZoom());
        // Get the current bounds of the map
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest().toJSON(); // Southwest corner
        const ne = bounds.getNorthEast().toJSON(); // Northeast corner
        fetchPoints(sw.lat, ne.lat, sw.lng, ne.lng, zoom);
    };

    // Fetch points for the current bounds using axios
    const fetchPoints = useCallback(
        debounce(async (lat_0, lat_1, lng_0, lng_1, zoom) => {
            try {
                const params = {
                    lat_0: lat_0 - 0.001,
                    lat_1: lat_1 + 0.001,
                    lng_0: lng_0 - 0.001,
                    lng_1: lng_1 + 0.001,
                    zoom: zoom
                }
                const response = await axios.get('map_data/dk', { params }); // Correctly pass params in an object
                setPoints(response.data); // Assuming the API returns points within the bounds
            } catch (error) {
                console.error('Error fetching points:', error);
            }
        }, 200), []); // Debounce with a delay of 2000ms

    // 4G network speed layer
    const rectangleLayer4G = new PolygonLayer({
        id: 'polygon-layer_4g',
        data: points,
        getPolygon: d => [
            [d[1] - rangelng, d[0] - rangelat], // bottom-left
            [d[1] + rangelng, d[0] - rangelat], // bottom-right
            [d[1] + rangelng, d[0] + rangelat], // top-right
            [d[1] - rangelng, d[0] + rangelat], // top-left
        ],
        getFillColor: (d) => [238, 250, 200, d[2] / 400 * 255], // Blue with transparency
        stroked: false,
        extruded: false,
        pickable: true
    });

    // 5G network speed layer
    const rectangleLayer5G = new PolygonLayer({
        id: 'polygon-layer_5g',
        data: points,
        getPolygon: d => [
            [d[1] - rangelng, d[0] - rangelat], // bottom-left
            [d[1] + rangelng, d[0] - rangelat], // bottom-right
            [d[1] + rangelng, d[0] + rangelat], // top-right
            [d[1] - rangelng, d[0] + rangelat], // top-left
        ],
        getFillColor: (d) => [192, 250, 0, d[3] / 700 * 255], // Blue with transparency
        stroked: false,
        extruded: false,
        pickable: true
    });

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <Map mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
                zoom={zoom} 
                center={center}
                reuseMaps={true}
                onCameraChanged={handleMapChange}
                options={{ streetViewControl: false, mapTypeControl: false }} >
                <DeckGLOverlay layers={[rectangleLayer4G, rectangleLayer5G]}
                    controller
                    getTooltip={({object}) => object && `lat: ${object[0]}\nlng: ${object[1]}\n4G: ${object[2]}MB/s\n5G: ${object[3]}MB/s`} />
            </Map>
        </APIProvider>
    );
};

export default DKMap;
